import moment, { Moment } from "moment-timezone";
import {
  isUtcIsoDateTime,
  isLegacyNaiveDateTime,
  getDisplayTimeZone,
} from "@/utils/dateTime/dateTime";
import { isTimezoneSupportEnabled } from "@/utils/timezone/timezone";

export const APPOINTMENT_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const envTimeZone = process.env.VUE_APP_TIME_ZONE;
export const APPOINTMENT_TIME_ZONE =
  envTimeZone && moment.tz.zone(envTimeZone) ? envTimeZone : "Europe/Dublin";

export type AppointmentTimeSource = {
  startTime?: string | null;
  endTime?: string | null;
  startDublinMs?: number | null;
};

export type AppointmentTimezoneInfo = {
  localTime: string;
  localZone: string;
  appointmentTime: string;
  appointmentZone: string;
};

export type AppointmentTimeDisplay = {
  localStartTime: Moment | null;
  localEndTime: Moment | null;
  appointmentStartTime: Moment | null;
  appointmentEndTime: Moment | null;
  timezoneInfo: AppointmentTimezoneInfo | null;
  shouldShowTimezoneInfo: boolean;
  canCancel: boolean;
};

export type AppointmentCountdownParts = {
  days: number;
  hours: string;
  minutes: string;
  seconds: string;
};

const warnedLegacyNaiveAppointmentValues = new Set<string>();

const warnLegacyNaiveAppointmentTime = (time: string): void => {
  // See warnLegacyNaiveDateTime — outside Australia the header is never sent, so a
  // naive value is expected rather than a backend gap worth reporting.
  if (!isTimezoneSupportEnabled()) return;
  if (warnedLegacyNaiveAppointmentValues.has(time)) return;
  warnedLegacyNaiveAppointmentValues.add(time);
  console.warn(
    `[appointmentTimezone] Received a naive appointment datetime with no timezone marker: "${time}". ` +
      `Interpreting as ${APPOINTMENT_TIME_ZONE} wall-clock time — this endpoint has not adopted the ` +
      "X-Response-Timezone header yet. Flag this endpoint to the backend team.",
  );
};

export const parseAppointmentTime = (time?: string | null): Moment | null => {
  if (!time) {
    return null;
  }

  if (isUtcIsoDateTime(time)) {
    const parsedUtcTime = moment.utc(time);
    return parsedUtcTime.isValid() ? parsedUtcTime : null;
  }

  if (isLegacyNaiveDateTime(time)) {
    warnLegacyNaiveAppointmentTime(time);
  }

  const parsedTime = moment.tz(
    time,
    APPOINTMENT_TIME_FORMAT,
    APPOINTMENT_TIME_ZONE,
  );

  return parsedTime.isValid() ? parsedTime : null;
};

export const formatAppointmentTimeForApi = (time?: string | null): string => {
  if (!time) return "";

  const parsedTime = parseAppointmentTime(time);
  if (!parsedTime) return time;

  return parsedTime.clone().tz(APPOINTMENT_TIME_ZONE).format(APPOINTMENT_TIME_FORMAT);
};

export const getDeviceTimeZone = (): string => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess()
    );
  } catch {
    return moment.tz.guess();
  }
};

// The member's stored preference wins over the device clock (TD-192) — the whole
// point of the setting is that a travelling member still sees their own times.
// Falls back to the device only while no preference has resolved.
export const getViewerTimeZone = (): string =>
  getDisplayTimeZone() || getDeviceTimeZone();

export const getAppointmentStartMilliseconds = (
  appointment: AppointmentTimeSource,
): number | null => {
  if (
    typeof appointment?.startDublinMs === "number" &&
    appointment.startDublinMs > 0
  ) {
    return appointment.startDublinMs;
  }

  const startTime = parseAppointmentTime(appointment?.startTime);

  return startTime ? startTime.valueOf() : null;
};

export const getMillisecondsUntilAppointment = (
  appointment: AppointmentTimeSource,
  nowMs = Date.now(),
): number | null => {
  const appointmentStartMs = getAppointmentStartMilliseconds(appointment);

  return appointmentStartMs === null ? null : appointmentStartMs - nowMs;
};

export const getAppointmentCountdownParts = (
  diffMs: number,
): AppointmentCountdownParts => {
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
};

const getComparableTimeRange = (
  startTime: Moment | null,
  endTime: Moment | null,
): string | null => {
  if (!startTime || !endTime) {
    return null;
  }

  return `${startTime.format("YYYY-MM-DD HH:mm")}-${endTime.format(
    "YYYY-MM-DD HH:mm",
  )}`;
};

const getDisplayTimeRange = (startTime: Moment, endTime: Moment): string => {
  if (startTime.isSame(endTime, "day")) {
    return `${startTime.format("DD MMM, H:mm")} - ${endTime.format("H:mm")}`;
  }

  return `${startTime.format("DD MMM, H:mm")} - ${endTime.format(
    "DD MMM, H:mm",
  )}`;
};

export const getAppointmentTimeDisplay = (
  appointment: AppointmentTimeSource,
  localTimeZone = getViewerTimeZone(),
  now = moment(),
): AppointmentTimeDisplay => {
  const startTime = parseAppointmentTime(appointment?.startTime);
  const endTime = parseAppointmentTime(appointment?.endTime);

  const localStartTime = startTime ? startTime.clone().tz(localTimeZone) : null;
  const localEndTime = endTime ? endTime.clone().tz(localTimeZone) : null;
  const appointmentStartTime = startTime
    ? startTime.clone().tz(APPOINTMENT_TIME_ZONE)
    : null;
  const appointmentEndTime = endTime
    ? endTime.clone().tz(APPOINTMENT_TIME_ZONE)
    : null;

  const localTimeRange = getComparableTimeRange(localStartTime, localEndTime);
  const appointmentTimeRange = getComparableTimeRange(
    appointmentStartTime,
    appointmentEndTime,
  );

  const timezoneInfo =
    localStartTime && localEndTime && appointmentStartTime && appointmentEndTime
      ? {
          localTime: getDisplayTimeRange(localStartTime, localEndTime),
          localZone: `${localTimeZone} (GMT${localStartTime.format("Z")})`,
          appointmentTime: getDisplayTimeRange(
            appointmentStartTime,
            appointmentEndTime,
          ),
          appointmentZone: `GMT${appointmentStartTime.format("Z")}`,
        }
      : null;

  return {
    localStartTime,
    localEndTime,
    appointmentStartTime,
    appointmentEndTime,
    timezoneInfo,
    shouldShowTimezoneInfo:
      !!timezoneInfo && localTimeRange !== appointmentTimeRange,
    canCancel: startTime ? startTime.isAfter(now) : true,
  };
};
